/*
 * GA4 event layer for shopbopis.com
 *
 * See shopbopis-ga4-README.md for the event list, the parameters each one
 * carries, and the custom dimensions that have to be registered in GA4
 * before any of those parameters show up in a report.
 *
 * Transport. Every event goes into window.dataLayer, which is what a GTM
 * container would read. There is no GTM container on this site today, so
 * a dataLayer push on its own reaches nothing. Events are therefore also
 * sent straight to gtag when gtag is present on the page.
 *
 * If a GTM container is ever added AND configured to forward these
 * events, that becomes a double count. Turn the direct path off by
 * setting window.SB_GA4_DIRECT_SEND = false before this file loads.
 *
 * Event naming. install_cta_click is the internal name. GA4 and Google
 * Ads already know this conversion as click_to_listing, so the direct
 * send renames it on the way out rather than breaking the configured
 * conversion action. Keep GA4_EVENT_ALIASES in sync if either name moves.
 */
(() => {
  window.dataLayer = window.dataLayer || [];

  const GA4_EVENT_ALIASES = {
    install_cta_click: 'click_to_listing',
  };

  const scrollMilestones = [25, 50, 75, 90];
  const engagedMilestones = [15, 30, 60, 120];
  const pushedScrollMilestones = new Set();
  const pushedEngagedMilestones = new Set();
  const startedForms = new WeakSet();
  let scrollRafId = null;
  let engagedMs = 0;
  let lastEngagementTick = Date.now();
  let didSendEngagementSummary = false;

  function getPageTemplate() {
    return document.querySelector('#MainContent')?.dataset?.template || '';
  }

  function getPagePath() {
    return `${window.location.pathname}${window.location.search}`;
  }

  function directSendEnabled() {
    return window.SB_GA4_DIRECT_SEND !== false && typeof window.gtag === 'function';
  }

  function sendToGtag(eventName, payload) {
    if (!directSendEnabled()) return;

    const outboundName = GA4_EVENT_ALIASES[eventName] || eventName;

    // gtag uses sendBeacon, so the hit survives the page being left. The
    // event and page fields are sent flat because GA4 only reads flat,
    // snake_case parameters.
    window.gtag('event', outboundName, payload);
  }

  function pushDataLayerEvent(eventName, params = {}) {
    if (!eventName) return;

    const payload = {
      page_template: getPageTemplate(),
      page_path: getPagePath(),
      page_title: document.title || '',
      page_location: window.location.href,
      ...params,
    };

    window.dataLayer.push({ event: eventName, ...payload });
    sendToGtag(eventName, payload);
  }

  function normalizeText(value) {
    return (value || '').replace(/\s+/g, ' ').trim();
  }

  function getElementLabel(element) {
    if (!element) return '';

    const sectionName = element.getAttribute('data-ga4-section-name');
    if (sectionName) return sectionName;

    if (element.id) return element.id;

    if (typeof element.className === 'string' && element.className.length > 0) {
      return element.className.split(/\s+/)[0] || '';
    }

    return (element.tagName || '').toLowerCase();
  }

  function getNearestSectionName(element) {
    if (!element) return '';

    const trackedSection = element.closest('[data-ga4-track-section]');
    if (trackedSection) return getElementLabel(trackedSection);

    const genericSection = element.closest('section');
    if (genericSection) return getElementLabel(genericSection);

    return '';
  }

  function toUrl(href) {
    try {
      return new URL(href, window.location.href);
    } catch (_error) {
      return null;
    }
  }

  function isInstallUrl(url) {
    if (!url) return false;
    return /(^|\.)apps\.shopify\.com$/i.test(url.hostname);
  }

  function isTrackableProtocol(url) {
    if (!url) return false;
    return !['mailto:', 'tel:', 'javascript:'].includes(url.protocol);
  }

  function trackInstallAndOutboundClicks(event) {
    const link = event.target instanceof Element ? event.target.closest('a[href]') : null;
    if (!link) return;

    const url = toUrl(link.getAttribute('href'));
    if (!isTrackableProtocol(url)) return;

    const ctaText =
      normalizeText(link.dataset.ga4CtaText) ||
      normalizeText(link.getAttribute('aria-label')) ||
      normalizeText(link.textContent);

    const ctaSection = link.dataset.ga4CtaSection || getNearestSectionName(link);
    const ctaId = link.dataset.ga4CtaId || link.id || '';
    const isInstallCta = link.hasAttribute('data-ga4-install-cta') || isInstallUrl(url);

    if (isInstallCta) {
      // cta_section and cta_id are the two that answer "which part of the
      // page sent them to the listing". Both need registering as custom
      // dimensions in GA4 or they are recorded and never reportable.
      pushDataLayerEvent('install_cta_click', {
        cta_text: ctaText,
        cta_section: ctaSection,
        cta_id: ctaId,
        cta_url: url?.href || '',
        link_domain: url?.hostname || '',
        link_target: link.target || '_self',
        has_gclid: sessionStorage.getItem('sb_gclid') ? 'yes' : 'no',
      });
      return;
    }

    if (url && url.origin !== window.location.origin) {
      pushDataLayerEvent('outbound_link_click', {
        link_text: ctaText,
        link_section: ctaSection,
        link_url: url.href,
        link_domain: url.hostname,
      });
    }
  }

  function trackScrollDepth() {
    scrollRafId = null;

    const scrollableHeight = document.documentElement.scrollHeight - window.innerHeight;
    if (scrollableHeight <= 0) return;

    const scrollTop = window.scrollY || document.documentElement.scrollTop || 0;
    const scrollPercent = Math.round((scrollTop / scrollableHeight) * 100);

    for (const milestone of scrollMilestones) {
      if (scrollPercent >= milestone && !pushedScrollMilestones.has(milestone)) {
        pushedScrollMilestones.add(milestone);
        pushDataLayerEvent('scroll_depth', { scroll_percent: milestone });
      }
    }
  }

  function queueScrollDepthCheck() {
    if (scrollRafId !== null) return;
    scrollRafId = window.requestAnimationFrame(trackScrollDepth);
  }

  function isEngagementActive() {
    return !document.hidden && document.hasFocus();
  }

  function syncEngagement() {
    const now = Date.now();
    if (isEngagementActive()) {
      engagedMs += Math.max(0, now - lastEngagementTick);
    }
    lastEngagementTick = now;

    const engagedSeconds = Math.floor(engagedMs / 1000);
    for (const milestone of engagedMilestones) {
      if (engagedSeconds >= milestone && !pushedEngagedMilestones.has(milestone)) {
        pushedEngagedMilestones.add(milestone);
        pushDataLayerEvent('engaged_time_milestone', {
          milestone_seconds: milestone,
          engaged_seconds: engagedSeconds,
        });
      }
    }
  }

  function sendEngagementSummaryOnce() {
    if (didSendEngagementSummary) return;
    didSendEngagementSummary = true;
    syncEngagement();
    pushDataLayerEvent('engagement_summary', {
      engaged_seconds: Math.floor(engagedMs / 1000),
    });
  }

  function observeSectionViews() {
    if (!('IntersectionObserver' in window)) return;

    const trackedSections = document.querySelectorAll('[data-ga4-track-section]');
    if (!trackedSections.length) return;

    const seenSections = new Set();
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;

          const sectionName = getElementLabel(entry.target);
          if (!sectionName || seenSections.has(sectionName)) continue;

          seenSections.add(sectionName);
          pushDataLayerEvent('section_view', { section_name: sectionName });
          observer.unobserve(entry.target);
        }
      },
      {
        threshold: 0.25,
      }
    );

    trackedSections.forEach((section) => observer.observe(section));
  }

  function getFormName(form) {
    return (
      form.getAttribute('data-ga4-form-name') ||
      form.getAttribute('name') ||
      form.id ||
      normalizeText(form.getAttribute('action')) ||
      'form'
    );
  }

  function trackFormStart(event) {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const form = target.closest('form');
    if (!form || startedForms.has(form)) return;

    startedForms.add(form);
    pushDataLayerEvent('form_start', {
      form_name: getFormName(form),
      form_id: form.id || '',
      form_action: form.getAttribute('action') || '',
      form_method: (form.getAttribute('method') || 'get').toLowerCase(),
      form_section: getNearestSectionName(form),
    });
  }

  function trackFormSubmit(event) {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    pushDataLayerEvent('form_submit', {
      form_name: getFormName(form),
      form_id: form.id || '',
      form_action: form.getAttribute('action') || '',
      form_method: (form.getAttribute('method') || 'get').toLowerCase(),
      form_section: getNearestSectionName(form),
    });
  }

  document.addEventListener('click', trackInstallAndOutboundClicks, { passive: true });
  window.addEventListener('scroll', queueScrollDepthCheck, { passive: true });
  document.addEventListener('focusin', trackFormStart);
  document.addEventListener('submit', trackFormSubmit, true);
  document.addEventListener('visibilitychange', syncEngagement);
  window.addEventListener('focus', syncEngagement);
  window.addEventListener('blur', syncEngagement);
  window.addEventListener('pagehide', sendEngagementSummaryOnce);

  setInterval(syncEngagement, 1000);

  trackScrollDepth();
  observeSectionViews();
})();
